'use client';

import React, { memo, useMemo } from 'react';
import AppIcon from './AppIcon';
import AppImage from './AppImage';

// logo aspect ratio: ~6:1 (width:height)
const LOGO_ASPECT = 2762 / 460;

interface AppLogoProps {
  src?: string;
  iconName?: string;
  height?: number;
  className?: string;
  onClick?: () => void;
  variant?: 'light' | 'dark';
}

const AppLogo = memo(function AppLogo({
  src,
  iconName = 'SparklesIcon',
  height = 36,
  className = '',
  onClick,
  variant = 'dark',
}: AppLogoProps) {
  const resolvedSrc = src ?? (
    variant === 'light'
      ? '/assets/images/logo_light.png'
      : '/assets/images/logo_dark.png'
  );
  const containerClassName = useMemo(() => {
    const classes = ['flex items-center'];
    if (onClick) classes.push('cursor-pointer hover:opacity-80 transition-opacity');
    if (className) classes.push(className);
    return classes.join(' ');
  }, [onClick, className]);

  const width = Math.round(height * LOGO_ASPECT);

  return (
    <div className={containerClassName} onClick={onClick}>
      {resolvedSrc ? (
        <AppImage
          src={resolvedSrc}
          alt="Logo"
          width={width}
          height={height}
          className="flex-shrink-0"
          style={{ height, width: 'auto' }}
          priority={true}
          unoptimized={resolvedSrc.endsWith('.svg')}
        />
      ) : (
        <AppIcon name={iconName} size={height} className="flex-shrink-0" />
      )}
    </div>
  );
});

export default AppLogo;
